import { sleep } from "@/lib/utils";

import type { Api, Order } from "../types";

const photo = (id: string) =>
	`https://images.unsplash.com/photo-${id}?w=200&h=260&fit=crop&q=80&fm=jpg&auto=format`;

// Realistic order history for the admin Orders tab — paid + declined, single-
// and multi-shop, so the table and detail view have something to render
// offline. Mirrors the gateway's Order shape exactly.
const fixtures: Order[] = [
	{
		id: "ord_8f2a1c4e9b6d0a3f",
		number: 100245,
		status: "paid",
		contact: { name: "Мария Соколова", email: "maria.s@example.com", phone: "+7 (921) 555-21-09" },
		address: {
			country: "Россия",
			city: "Санкт-Петербург",
			street: "наб. реки Фонтанки, 24, кв. 7",
			postalCode: "191023",
		},
		delivery: "express",
		payment: { status: "approved", brand: "Visa", last4: "4242" },
		groups: [
			{
				shopId: "shop-silver-moon",
				shopName: "Silver Moon",
				shopSlug: "silver-moon",
				items: [
					{
						productId: "prod-1",
						name: "Серьги из серебра ручной работы",
						brand: "Silver Moon",
						photo: photo("1535632787350-4e68ef0ac584"),
						size: "Универсальный",
						color: "Серебряный",
						qty: 1,
						unitPrice: 3500,
						lineTotal: 3500,
					},
				],
				subtotal: 3500,
				deliveryFee: 700,
			},
		],
		subtotal: 3500,
		deliveryTotal: 700,
		total: 4200,
		itemCount: 1,
		createdAt: "2026-05-24T13:42:00Z",
	},
	{
		id: "ord_3d7b9e0f1a2c5d8e",
		number: 100246,
		status: "paid",
		contact: { name: "Алексей Кузнецов", email: "a.kuznetsov@example.com", phone: "+7 (905) 412-88-30" },
		address: {
			country: "Россия",
			city: "Москва",
			street: "ул. Тверская, 10, кв. 45",
			postalCode: "125009",
			comment: "Позвонить за час до доставки",
		},
		delivery: "standard",
		payment: { status: "approved", brand: "Visa", last4: "4242" },
		groups: [
			{
				shopId: "shop-keramika",
				shopName: "Керамика Плюс",
				shopSlug: "keramika-plus",
				items: [
					{
						productId: "prod-2",
						name: "Керамическая ваза «Волна»",
						brand: "Керамика Плюс",
						photo: photo("1618221639244-c1a8502c0eb9"),
						size: "35 см",
						color: "Белый",
						qty: 2,
						unitPrice: 4800,
						lineTotal: 9600,
					},
				],
				subtotal: 9600,
				deliveryFee: 300,
			},
			{
				shopId: "shop-uyut",
				shopName: "Уютный дом",
				shopSlug: "uyutny-dom",
				items: [
					{
						productId: "prod-20",
						name: "Плед из мериносовой шерсти",
						brand: "Уютный дом",
						photo: photo("1584100936595-c0654b55a2e2"),
						size: "150x200 см",
						color: "Серый",
						qty: 1,
						unitPrice: 12500,
						lineTotal: 12500,
					},
				],
				subtotal: 12500,
				deliveryFee: 300,
			},
		],
		subtotal: 22100,
		deliveryTotal: 600,
		total: 22700,
		itemCount: 3,
		createdAt: "2026-05-22T09:15:00Z",
	},
	{
		id: "ord_5a1f8c2b4e6d7090",
		number: 100247,
		status: "declined",
		contact: { name: "Ольга Иванова", email: "olga.iv@example.com", phone: "+7 (916) 200-14-77" },
		address: {
			country: "Россия",
			city: "Казань",
			street: "ул. Баумана, 3",
			postalCode: "420111",
		},
		delivery: "pickup",
		payment: { status: "declined", brand: "Visa", last4: "0002" },
		groups: [
			{
				shopId: "shop-natur",
				shopName: "НатурТекстиль",
				shopSlug: "natur-tekstil",
				items: [
					{
						productId: "prod-3",
						name: "Вязаный свитер оверсайз",
						brand: "НатурТекстиль",
						photo: photo("1576566588028-4147f3842f27"),
						size: "M",
						color: "Бежевый",
						qty: 1,
						unitPrice: 7200,
						lineTotal: 7200,
					},
				],
				subtotal: 7200,
				deliveryFee: 0,
			},
		],
		subtotal: 7200,
		deliveryTotal: 0,
		total: 7200,
		itemCount: 1,
		createdAt: "2026-05-21T18:03:00Z",
	},
];

export const orders: Api["orders"] = {
	async list() {
		await sleep(160);
		return [...fixtures].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
	},
	async page(filter) {
		await sleep(160);
		const q = filter.search?.trim().toLowerCase();
		const rows = [...fixtures]
			.filter((o) => {
				if (filter.id && o.id !== filter.id) return false;
				if (filter.statuses?.length && !filter.statuses.includes(o.status)) return false;
				if (filter.shopId && !o.groups.some((g) => g.shopId === filter.shopId)) return false;
				const day = o.createdAt.slice(0, 10);
				if (filter.from && day < filter.from) return false;
				if (filter.to && day > filter.to) return false;
				return !q || JSON.stringify(o).toLowerCase().includes(q);
			})
			.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
		const start = (filter.page - 1) * filter.pageSize;
		return {
			items: rows.slice(start, start + filter.pageSize),
			total: rows.length,
			page: filter.page,
			pageSize: filter.pageSize,
			totalPages: Math.ceil(rows.length / filter.pageSize),
		};
	},
	async statusCounts(query) {
		await sleep(80);
		const counts: Record<string, number> = {};
		for (const o of fixtures) {
			if (query.shopId && !o.groups.some((g) => g.shopId === query.shopId)) continue;
			counts[o.status] = (counts[o.status] ?? 0) + 1;
		}
		return counts;
	},
	async shops() {
		await sleep(60);
		const map = new Map(fixtures.flatMap((o) => o.groups.map((g) => [g.shopId, g.shopName] as const)));
		return [...map].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "ru"));
	},
	// The offline admin has no carrier integration — there's nothing to track.
	async shipments() {
		await sleep(120);
		return [];
	},
	// Офлайн-заглушка: помечаем заказ ЮЛ оплаченным по счёту.
	async confirmInvoicePayment(orderId: string) {
		await sleep(140);
		const order = fixtures.find((o) => o.id === orderId);
		if (!order) throw new Error("Order not found");
		return { ...order, payment: { ...order.payment, status: "approved" } };
	},
	async setStatus(orderId, input) {
		await sleep(140);
		const order = fixtures.find((o) => o.id === orderId);
		if (!order) throw new Error("Order not found");
		order.status = input.status;
		return { ...order };
	},
	async receipt(orderId: string) {
		await sleep(100);
		const order = fixtures.find((o) => o.id === orderId);
		if (!order) throw new Error("Order not found");
		return {
			Items: [
				...order.groups.flatMap((g) =>
					g.items.map((it) => ({ label: it.name, price: it.unitPrice, quantity: it.qty, amount: it.lineTotal })),
				),
				...(order.deliveryTotal > 0
					? [{ label: "Доставка", price: order.deliveryTotal, quantity: 1, amount: order.deliveryTotal }]
					: []),
			],
			email: order.contact.email,
			phone: order.contact.phone,
			amounts: { electronic: order.total },
		};
	},
};
